import React, { useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { BarChart3 } from 'lucide-react';
import {
  BOT_COLORS, BOT_LABELS,
  formatUSD, formatUSDCompact, formatPct, formatNum,
} from './ops_helpers';
import { TimeframeSelector } from './BotAnalytics';

const tooltipStyle = {
  backgroundColor: '#0a0a0b',
  border: '1px solid #3f3f46',
  borderRadius: '8px',
  fontSize: '12px',
};

/**
 * Normaliza las series cumulative_pnl de cada bot a una grilla de timestamps unificada.
 * Como las series tienen timestamps por trade, las muestreamos por día.
 */
function buildCombinedSeries(bots) {
  const buckets = {}; // day -> { day, [bot_id]: cum_pnl }
  bots.forEach((b) => {
    let lastVal = 0;
    (b.cumulative_pnl || []).forEach((p) => {
      const day = (p.t || '').slice(0, 10);
      if (!day) return;
      lastVal = p.cum_pnl;
      buckets[day] = buckets[day] || { day };
      buckets[day][b.bot_id] = p.cum_pnl;
    });
    // Fill-forward: en días sin trade, mantener último valor
    // (lo hacemos en post-pass abajo)
  });
  const sorted = Object.values(buckets).sort((a, b) => a.day.localeCompare(b.day));
  // Forward-fill por bot
  const lastByBot = {};
  sorted.forEach((row) => {
    bots.forEach((b) => {
      if (row[b.bot_id] != null) lastByBot[b.bot_id] = row[b.bot_id];
      else if (lastByBot[b.bot_id] != null) row[b.bot_id] = lastByBot[b.bot_id];
    });
  });
  return sorted;
}

const ScoreCell = ({ value, threshold = 0, format = (v) => v, suffix = '' }) => {
  const v = typeof value === 'number' ? value : parseFloat(value) || 0;
  const tone = v > threshold ? 'text-emerald-400' : v < threshold ? 'text-rose-400' : 'text-zinc-300';
  return <span className={`font-semibold ${tone}`}>{format(v)}{suffix}</span>;
};

export const BotComparison = ({ days, onDaysChange, comparison, loading }) => {
  const bots = useMemo(() => comparison?.bots || [], [comparison]);
  const combined = useMemo(() => buildCombinedSeries(bots), [bots]);

  return (
    <div className="space-y-4" data-testid="bot-comparison">
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardContent className="p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-violet-400" />
            <span className="text-xs text-zinc-400 font-medium">Comparativa global · Timeframe:</span>
            <TimeframeSelector value={days} onChange={onDaysChange} testIdPrefix="compare-tf" />
          </div>
          <Badge variant="outline" className="text-xs border-zinc-700">
            {bots.length} bots
          </Badge>
        </CardContent>
      </Card>

      {/* PnL acumulado normalizado side-by-side */}
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-100 text-base">PnL acumulado por bot</CardTitle>
          <CardDescription>Por día · cada línea representa un bot</CardDescription>
        </CardHeader>
        <CardContent>
          {combined.length < 2 ? (
            <p className="text-center text-zinc-500 py-10 text-sm">
              No hay suficientes trades para comparar. Espera a que se acumulen al menos 2 días.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={combined} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="day" stroke="#71717a" tick={{ fontSize: 10 }} />
                <YAxis stroke="#71717a" tick={{ fontSize: 10 }} tickFormatter={(v) => formatUSDCompact(v)} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n) => [formatUSD(v), BOT_LABELS[n] || n]}
                />
                <Legend formatter={(v) => <span className="text-zinc-300 text-xs">{BOT_LABELS[v] || v}</span>} />
                <ReferenceLine y={0} stroke="#52525b" strokeDasharray="3 3" />
                {bots.map((b) => (
                  <Line
                    key={b.bot_id}
                    type="monotone"
                    dataKey={b.bot_id}
                    stroke={BOT_COLORS[b.bot_id] || '#a855f7'}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* KPI comparison table */}
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-100 text-base">KPIs comparados</CardTitle>
          <CardDescription>Mismo timeframe en todos los bots</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-12 bg-zinc-900/60 border border-zinc-800 rounded animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400 text-xs">Bot</TableHead>
                    <TableHead className="text-zinc-400 text-xs text-right">Equity</TableHead>
                    <TableHead className="text-zinc-400 text-xs text-right">Net PnL</TableHead>
                    <TableHead className="text-zinc-400 text-xs text-right">Trades</TableHead>
                    <TableHead className="text-zinc-400 text-xs text-right">Win Rate</TableHead>
                    <TableHead className="text-zinc-400 text-xs text-right">PF</TableHead>
                    <TableHead className="text-zinc-400 text-xs text-right">Exp.</TableHead>
                    <TableHead className="text-zinc-400 text-xs text-right">Avg R</TableHead>
                    <TableHead className="text-zinc-400 text-xs text-right">Max DD</TableHead>
                    <TableHead className="text-zinc-400 text-xs text-right">Sharpe</TableHead>
                    <TableHead className="text-zinc-400 text-xs text-right">Sortino</TableHead>
                    <TableHead className="text-zinc-400 text-xs text-right">Racha W/L</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bots.map((b) => (
                    <TableRow key={b.bot_id} className="border-zinc-800 hover:bg-zinc-800/30">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: BOT_COLORS[b.bot_id] || '#a855f7' }}
                          />
                          <div>
                            <div className="text-zinc-100 font-semibold text-sm">{BOT_LABELS[b.bot_id] || b.bot_id}</div>
                            <div className="text-[10px] text-zinc-500">v{b.version} · {b.host || 'n/a'}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-zinc-200 font-mono text-xs">
                        {formatUSD(b.equity_usd || 0)}
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        <ScoreCell value={b.net_pnl} format={(v) => `${v >= 0 ? '+' : ''}${formatUSD(v)}`} />
                      </TableCell>
                      <TableCell className="text-right text-zinc-300 text-xs">{b.trades_count}</TableCell>
                      <TableCell className="text-right text-xs">
                        <ScoreCell value={b.win_rate} threshold={50} format={(v) => formatPct(v, 1)} />
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        <ScoreCell value={b.profit_factor || 0} threshold={1} format={(v) => v === 0 ? '—' : formatNum(v, 2)} />
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        <ScoreCell value={b.expectancy} format={(v) => formatUSD(v)} />
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        <ScoreCell value={b.avg_r} format={(v) => formatNum(v, 2)} />
                      </TableCell>
                      <TableCell className="text-right text-rose-400 text-xs font-semibold">
                        {formatPct(b.max_drawdown_pct || 0, 2)}
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        <ScoreCell value={b.sharpe} format={(v) => formatNum(v, 2)} />
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        <ScoreCell value={b.sortino} format={(v) => formatNum(v, 2)} />
                      </TableCell>
                      <TableCell className="text-right text-zinc-300 text-xs font-mono">
                        {b.max_win_streak}/{b.max_loss_streak}
                      </TableCell>
                    </TableRow>
                  ))}
                  {bots.length === 0 && (
                    <TableRow><TableCell colSpan={12} className="text-center text-zinc-500 py-10 text-xs">
                      Sin bots con datos.
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
